/**
 * FamilySelector — data-driven, keyboard-accessible tab rail.
 *
 * Desktop: a 2-column technical tab list with a small luminous marker + thin
 * family-colored line on the active item.
 * Mobile: the same list becomes a horizontally scrollable chip rail.
 *
 * The selector never assumes a fixed length — pass any number of families.
 */
export function FamilySelector({ families, activeId, onSelect }) {
  const onKeyDown = (e, idx) => {
    if (e.key === "ArrowDown" || e.key === "ArrowRight") {
      e.preventDefault();
      onSelect(families[(idx + 1) % families.length].id);
    } else if (e.key === "ArrowUp" || e.key === "ArrowLeft") {
      e.preventDefault();
      onSelect(families[(idx - 1 + families.length) % families.length].id);
    } else if (e.key === "Home") {
      e.preventDefault();
      onSelect(families[0].id);
    } else if (e.key === "End") {
      e.preventDefault();
      onSelect(families[families.length - 1].id);
    }
  };

  // Prevent horizontal swipe inside the mobile chip rail from bubbling
  // up to the parent Lore carousel (which listens to pointer events on
  // `.prophets-insight-track`). Without this, dragging through the
  // family list would accidentally paginate the outer slider.
  const stopSwipeBubble = (e) => {
    e.stopPropagation();
  };

  return (
    <div
      role="tablist"
      aria-label="Prophet families"
      data-testid="family-selector"
      className="prophets-lore-selector"
      onPointerDown={stopSwipeBubble}
      onPointerMove={stopSwipeBubble}
      onPointerUp={stopSwipeBubble}
      onPointerCancel={stopSwipeBubble}
      onTouchStart={stopSwipeBubble}
      onTouchMove={stopSwipeBubble}
      onTouchEnd={stopSwipeBubble}
    >
      {families.map((f, idx) => {
        const isActive = f.id === activeId;
        return (
          <button
            key={f.id}
            type="button"
            role="tab"
            aria-selected={isActive}
            aria-controls="family-profile-panel"
            tabIndex={isActive ? 0 : -1}
            data-testid={`family-selector-${f.id}`}
            onClick={() => onSelect(f.id)}
            onKeyDown={(e) => onKeyDown(e, idx)}
            className={`prophets-lore-selector-item ${isActive ? "is-active" : ""}`}
            style={{ "--family-accent-item": f.accent }}
          >
            <span aria-hidden="true" className="prophets-lore-selector-marker" />
            <span className="p-mono prophets-lore-selector-index">
              {String(f.index).padStart(2, "0")}
            </span>
            <span className="p-nav prophets-lore-selector-name">{f.name}</span>
            <span aria-hidden="true" className="prophets-lore-selector-line" />
          </button>
        );
      })}
    </div>
  );
}
